import { create } from 'zustand';
import * as pdfService from '../services/pdfService';
import useNotificationStore from './notificationStore';

const usePDFStore = create((set, get) => ({
  // State
  pdfs: [],
  currentPdf: null,
  categories: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    status: 'all',
    category: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 20,
    sharedWithMe: false
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  analytics: null,
  
  // Actions
  
  // Fetch PDFs with filters
  fetchPDFs: async (filters = {}) => {
    set({ loading: true, error: null });
    
    try {
      const currentFilters = { ...get().filters, ...filters };
      const response = await pdfService.getPDFs(currentFilters);
      
      set({
        pdfs: response.data.pdfs,
        pagination: response.data.pagination,
        filters: currentFilters,
        loading: false
      });
      
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      
      // Handle session expiration gracefully
      if (error.status === 401 || error.message === 'Session expired. Please login again.') {
        set({ pdfs: [], pagination: null });
        return { pdfs: [], pagination: null };
      }
      
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Fetch single PDF
  fetchPDF: async (id) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.getPDF(id);
      
      set({
        currentPdf: response.data.pdf,
        loading: false
      });
      
      return response.data.pdf;
    } catch (error) {
      set({ error: error.message, loading: false });
      
      // Handle session expiration gracefully
      if (error.status === 401 || error.message === 'Session expired. Please login again.') {
        set({ currentPdf: null });
        return null;
      }
      
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Create PDF
  createPDF: async (data) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.createPDF(data);
      const newPdf = response.data.pdf;
      
      set(state => ({
        pdfs: [newPdf, ...state.pdfs],
        currentPdf: newPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF created successfully');
      return newPdf;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Update PDF
  updatePDF: async (id, data) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.updatePDF(id, data);
      const updatedPdf = response.data.pdf;
      
      set(state => ({
        pdfs: state.pdfs.map(pdf => 
          pdf._id === id ? updatedPdf : pdf
        ),
        currentPdf: state.currentPdf?._id === id ? updatedPdf : state.currentPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF updated successfully');
      return updatedPdf;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Delete PDF
  deletePDF: async (id) => {
    set({ loading: true, error: null });
    
    try {
      await pdfService.deletePDF(id);
      
      set(state => ({
        pdfs: state.pdfs.filter(pdf => pdf._id !== id),
        currentPdf: state.currentPdf?._id === id ? null : state.currentPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF deleted successfully');
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Generate PDF file
  generatePDF: async (id) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.generatePDF(id);
      
      // Update the PDF with new URL
      if (get().currentPdf?._id === id) {
        set(state => ({
          currentPdf: {
            ...state.currentPdf,
            currentPdfUrl: response.data.url
          }
        }));
      }
      
      // Update in list
      set(state => ({
        pdfs: state.pdfs.map(pdf => 
          pdf._id === id 
            ? { ...pdf, currentPdfUrl: response.data.url }
            : pdf
        ),
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF generated successfully');
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Duplicate PDF
  duplicatePDF: async (id) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.duplicatePDF(id);
      const duplicatedPdf = response.data.pdf;
      
      set(state => ({
        pdfs: [duplicatedPdf, ...state.pdfs],
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF duplicated successfully');
      return duplicatedPdf;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Share PDF
  sharePDF: async (id, users) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.sharePDF(id, users);
      const updatedPdf = response.data.pdf;
      
      set(state => ({
        pdfs: state.pdfs.map(pdf => 
          pdf._id === id ? updatedPdf : pdf
        ),
        currentPdf: state.currentPdf?._id === id ? updatedPdf : state.currentPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('PDF shared successfully');
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Remove share
  unsharePDF: async (pdfId, userId) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.unsharePDF(pdfId, userId);
      const updatedPdf = response.data.pdf;
      
      set(state => ({
        pdfs: state.pdfs.map(pdf => 
          pdf._id === pdfId ? updatedPdf : pdf
        ),
        currentPdf: state.currentPdf?._id === pdfId ? updatedPdf : state.currentPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('Share removed successfully');
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Fetch analytics
  fetchAnalytics: async (id, dateRange, granularity = 'day') => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.getPDFAnalytics(id, dateRange, granularity);
      
      set({
        analytics: response.data,
        loading: false
      });
      
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Track event
  trackEvent: async (pdfId, event, eventData) => {
    try {
      await pdfService.trackPDFEvent(pdfId, event, eventData);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  },
  
  // Fetch categories
  fetchCategories: async () => {
    try {
      const response = await pdfService.getCategories();
      set({ categories: response.data.categories });
      return response.data.categories;
    } catch (error) {
      // Handle session expiration and authentication errors gracefully
      if (error.status === 401 || 
          error.message === 'Session expired. Please login again.' ||
          error.message === 'Authentication required') {
        set({ categories: [] });
        return [];
      }
      
      // Only show error for non-auth issues
      useNotificationStore.getState().showError('Failed to fetch categories');
      throw error;
    }
  },
  
  // Create category
  createCategory: async (data) => {
    try {
      const response = await pdfService.createCategory(data);
      const newCategory = response.data.category;
      
      set(state => ({
        categories: [...state.categories, newCategory]
      }));
      
      useNotificationStore.getState().showSuccess('Category created successfully');
      return newCategory;
    } catch (error) {
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Update category
  updateCategory: async (id, data) => {
    try {
      const response = await pdfService.updateCategory(id, data);
      const updatedCategory = response.data.category;
      
      set(state => ({
        categories: state.categories.map(cat => 
          cat._id === id ? updatedCategory : cat
        )
      }));
      
      useNotificationStore.getState().showSuccess('Category updated successfully');
      return updatedCategory;
    } catch (error) {
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Delete category
  deleteCategory: async (id) => {
    try {
      await pdfService.deleteCategory(id);
      
      set(state => ({
        categories: state.categories.filter(cat => cat._id !== id)
      }));
      
      useNotificationStore.getState().showSuccess('Category deleted successfully');
    } catch (error) {
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Set filters
  setFilters: (filters) => {
    set(state => ({
      filters: { ...state.filters, ...filters }
    }));
  },
  
  // Clear filters
  clearFilters: () => {
    set({
      filters: {
        search: '',
        status: 'all',
        category: 'all',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        page: 1,
        limit: 20,
        sharedWithMe: false
      }
    });
  },
  
  // Clear current PDF
  clearCurrentPDF: () => {
    set({ currentPdf: null });
  },
  
  // Revoke public access
  revokePublicAccess: async (id) => {
    set({ loading: true, error: null });
    
    try {
      const response = await pdfService.revokePublicAccess(id);
      const updatedPdf = response.data.pdf;
      
      set(state => ({
        pdfs: state.pdfs.map(pdf => 
          pdf._id === id ? updatedPdf : pdf
        ),
        currentPdf: state.currentPdf?._id === id ? updatedPdf : state.currentPdf,
        loading: false
      }));
      
      useNotificationStore.getState().showSuccess('Public access revoked');
      return updatedPdf;
    } catch (error) {
      set({ error: error.message, loading: false });
      useNotificationStore.getState().showError(error.message);
      throw error;
    }
  },
  
  // Reset store
  reset: () => {
    set({
      pdfs: [],
      currentPdf: null,
      categories: [],
      loading: false,
      error: null,
      filters: {
        search: '',
        status: 'all',
        category: 'all',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        page: 1,
        limit: 20,
        sharedWithMe: false
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      },
      analytics: null
    });
  }
}));

export default usePDFStore;